import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { AdminAuthService } from '../../services/auth/admin-auth.service';
import { RouterUrlsEnum } from '../../enums/router-urls.enum';
import { userDisplayName, userInitials } from '../../helper/user-display';
import { performLogout } from '../../helper/session';

@Component({
  selector: 'bodyanalytics-profile-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './profile-menu.component.html',
  styleUrls: ['./profile-menu.component.scss'],
  imports: [RouterLink, MatIconModule],
})
export class ProfileMenuComponent {
  private auth = inject(AuthService);
  private adminAuthService = inject(AdminAuthService);
  private elementRef = inject(ElementRef);

  /**
   * Account-settings destination. Defaults to the customer app's real route
   * (AUTH_ACCOUNT, '/auth/account'). Hosts whose account page lives elsewhere —
   * e.g. AdminShellComponent, where account settings are registered under
   * profile-ui's `auth/profile/details` (#2136) — must override this input, or the
   * link 404s and falls through to that app's wildcard redirect (#1985 follow-up).
   */
  readonly accountUrl = input<string>(RouterUrlsEnum.AUTH_ACCOUNT);
  protected readonly open = signal(false);
  protected readonly user = toSignal(this.auth.user$);

  protected readonly displayName = computed(() => userDisplayName(this.user()));
  protected readonly initials = computed(() => userInitials(this.user()));

  protected toggle() {
    this.open.update(value => !value);
  }

  protected logout() {
    this.open.set(false);
    performLogout(this.auth, this.adminAuthService);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent) {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape() {
    this.open.set(false);
  }
}
